"use client";

/* ═══════════ Blocs partagés des 5 sites — cartes, bandes, salons communs ═══════════ */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Heart, Moon, Play, ShoppingBag, Sparkles, Star, Eye } from "lucide-react";
import {
  MARQUES, PACKS, PORTAILS, MAWLID, VIDEO, SIGNATURE, TOTAL_VISUELS,
  type Marque, type Produit, type Vedette,
} from "@/data/catalog";
import { COUNTDOWN_CIBLE } from "@/data/sites";
import { formatPrix, prixProduit } from "./types";
import { Crown } from "./ui-extras";

/* ─────────────── Aides ─────────────── */
export function ordinalFr(n: number): string {
  if (n === 1) return "1ʳᵉ";
  return String(n) + "ᵉ";
}

export function normaliser(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

export type FormatFiltre = "tous" | "33" | "50" | "75" | "grand";

export const FORMATS: { valeur: FormatFiltre; label: string }[] = [
  { valeur: "tous", label: "Tous" },
  { valeur: "33", label: "Canette 33 cl" },
  { valeur: "50", label: "Bouteille 50 cl" },
  { valeur: "75", label: "Flacon 75 cl" },
  { valeur: "grand", label: "Grand format" },
];

export function matchFormat(contenance: string, f: FormatFiltre): boolean {
  if (f === "tous") return true;
  if (f === "grand") return /\b(1|2|3)\s*L\b/i.test(contenance) || contenance.includes("1,5");
  return contenance.includes(f);
}

export function trierProduits(list: Produit[], tri: "catalogue" | "az" | "za"): Produit[] {
  const l = [...list];
  if (tri === "az") return l.sort((a, b) => a.name.localeCompare(b.name, "fr"));
  if (tri === "za") return l.sort((a, b) => b.name.localeCompare(a.name, "fr"));
  return l.sort((a, b) => a.num - b.num);
}

/* ─────────────── Carte produit — façon catalogue royal ─────────────── */
interface CarteProps {
  marque: Marque;
  produit: Produit;
  rang?: number;
  favori: boolean;
  onFavori: () => void;
  onVoir: () => void;
  onAdd: () => void;
  sousTitre?: string;
}

export function CarteProduit({ marque, produit, rang, favori, onFavori, onVoir, onAdd, sousTitre }: CarteProps) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      className="card-empire rounded-2xl overflow-hidden group flex flex-col"
    >
      <div className="relative">
        <button
          onClick={onVoir}
          className="block w-full focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60"
          aria-label={"Voir en grand " + produit.name}
        >
          <img
            src={produit.file}
            alt={produit.name + " — " + marque.nom}
            loading="lazy"
            className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </button>
        <span className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase bg-[#170d02]/85 border border-[#D4AF37]/50 text-[#F6E27A]">
          N° {String(produit.num).padStart(2, "0")}
        </span>
        <div className="absolute top-2.5 right-2.5 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button
            onClick={onFavori}
            aria-label={favori ? "Retirer " + produit.name + " des favoris" : "Ajouter " + produit.name + " aux favoris"}
            aria-pressed={favori}
            className={"h-8 w-8 rounded-full border flex items-center justify-center transition-colors " +
              (favori
                ? "bg-[#D4AF37] border-[#D4AF37] text-[#170d02]"
                : "bg-[#170d02]/80 border-[#D4AF37]/50 text-[#F6E27A] hover:bg-[#D4AF37] hover:text-[#170d02]")}
          >
            <Heart className="h-4 w-4" fill={favori ? "currentColor" : "none"} />
          </button>
          <button
            onClick={onVoir}
            aria-label="Agrandir la création"
            className="h-8 w-8 rounded-full bg-[#170d02]/80 border border-[#D4AF37]/50 text-[#F6E27A] hover:bg-[#D4AF37] hover:text-[#170d02] flex items-center justify-center transition-colors"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h4 className="font-display font-bold text-[#F5EFE0] leading-snug">{produit.name}</h4>
        <p className="text-xs text-[#F5EFE0]/50 truncate">
          {sousTitre ? sousTitre : (rang ? ordinalFr(rang) + " maison — " : "") + marque.nom}
          {produit.contenance ? " · " + produit.contenance : ""}
        </p>
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <span className="font-display text-lg font-bold gold-text">{formatPrix(prixProduit(produit.num))}</span>
          <button
            onClick={onAdd}
            className="gold-btn px-3.5 py-2 rounded-xl font-semibold text-xs inline-flex items-center gap-1.5"
            aria-label={"Ajouter " + produit.name + " au panier"}
          >
            <ShoppingBag className="h-3.5 w-3.5" /> Panier
          </button>
        </div>
      </div>
    </motion.article>
  );
}

/* ─────────────── Bloc — compte à rebours royal ─────────────── */
function resteCalcul() {
  const diff = Math.max(0, new Date(COUNTDOWN_CIBLE).getTime() - Date.now());
  return {
    j: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

export function BlockCountdown() {
  const [reste, setReste] = useState(resteCalcul);

  useEffect(() => {
    const id = window.setInterval(() => setReste(resteCalcul()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const cases: [string, number][] = [["Jours", reste.j], ["Heures", reste.h], ["Minutes", reste.m], ["Secondes", reste.s]];

  return (
    <section className="border-y border-[#D4AF37]/20 bg-[#03150d]/70" aria-label="Compte à rebours royal">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 text-center">
        <p className="tracking-[0.35em] text-[#F6E27A] text-xs uppercase mb-3 flex items-center justify-center gap-2">
          <Moon className="h-4 w-4" /> Mawlid Naby <Star className="inline h-3 w-3 -mt-1" /> Gamou — l&rsquo;an 2027
        </p>
        <h2 className="font-display text-2xl md:text-4xl font-bold text-[#F5EFE0] mb-2">
          Le <span className="gold-text">Compte à Rebours</span> Royal
        </h2>
        <p className="text-sm text-[#F5EFE0]/55 mb-8">
          Les rideaux se rouvrent pour les grandes nuits — préparons déjà les coffrets de gala.
        </p>
        <div className="grid grid-cols-4 gap-3 max-w-2xl mx-auto">
          {cases.map(([l, v]) => (
            <div key={l} className="card-empire rounded-2xl py-5 px-2">
              <p className="font-display text-3xl md:text-5xl font-black gold-text tabular-nums">
                {String(v).padStart(2, "0")}
              </p>
              <p className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#F5EFE0]/60 mt-1">{l}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#F5EFE0]/35 italic mt-6">
          Dates cérémonielles annoncées par la commission lunaire de l&rsquo;Empire.
        </p>
      </div>
    </section>
  );
}

/* ─────────────── Bloc — bande royale des vedettes ─────────────── */
interface BandeProps {
  vedettes: Vedette[];
  titre?: string;
  sous?: string;
  onAdd: (v: Vedette) => void;
  onVoir: (v: Vedette) => void;
}

export function BlockVedettes({ vedettes, titre, sous, onAdd, onVoir }: BandeProps) {
  return (
    <section className="py-16 md:py-20 border-t border-[#D4AF37]/15" aria-label="Bande royale des vedettes">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-10 text-center">
        <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3 flex items-center justify-center gap-2">
          <Sparkles className="h-4 w-4" /> La bande royale des merveilles
        </p>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-[#F5EFE0]">
          {titre ? titre : <>Les <span className="gold-text">Vedettes</span> du site</>}
        </h2>
        {sous && <p className="text-[#F5EFE0]/60 max-w-2xl mx-auto mt-3">{sous}</p>}
        <div className="gold-line w-72 mx-auto mt-5" />
      </div>
      <div className="empire-scroll overflow-x-auto pb-4">
        <div className="flex gap-5 px-4 sm:px-6 w-max mx-auto">
          {vedettes.map((v, i) => (
            <motion.figure
              key={v.marqueSlug + v.num}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.3) }}
              className="card-empire rounded-2xl overflow-hidden w-60 sm:w-64 shrink-0 snap-center bg-[#03150d]/60"
            >
              <button onClick={() => onVoir(v)} className="block w-full" aria-label={"Voir " + v.nom}>
                <img src={v.file} alt={v.nom + " — " + v.marque} loading="lazy" className="w-full aspect-square object-cover" />
              </button>
              <figcaption className="p-4 space-y-2">
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">{v.marque}</p>
                <h3 className="font-display font-bold text-[#F5EFE0] leading-snug">{v.nom}</h3>
                <p className="text-xs text-[#F5EFE0]/55 italic">{v.accroche}</p>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="font-display text-lg font-bold gold-text">{formatPrix(prixProduit(v.num))}</span>
                  <button
                    onClick={() => onAdd(v)}
                    className="gold-btn px-3.5 py-2 rounded-xl font-semibold text-xs inline-flex items-center gap-1.5"
                    aria-label={"Ajouter " + v.nom + " au panier"}
                  >
                    <ShoppingBag className="h-3.5 w-3.5" /> Panier
                  </button>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────── Bloc — chapitre Mawlid Naby – Gamou (place du bordeaux) ─────────────── */
interface MawlidProps {
  onAdd: (m: Marque, p: Produit) => void;
}

export function BlockMawlid({ onAdd }: MawlidProps) {
  const bouye = MARQUES.find((m) => m.slug === "bouye-ndiamsyl")!;
  const coffrets = bouye.produits.filter((p) => [97, 99, 88, 33].includes(p.num));

  return (
    <section className="bordeaux-band" aria-label="Chapitre Mawlid Naby et Gamou">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
        <div className="text-center mb-12">
          <p className="tracking-[0.35em] text-[#F6E27A] text-xs uppercase mb-3 flex items-center justify-center gap-2">
            <Moon className="h-4 w-4" /> Chapitre d&rsquo;ouverture — Mawlid Naby – Gamou
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[#F5EFE0]">
            Les <span className="gold-text">Coffrets Bouye</span> précèdent la marche
          </h2>
          <p className="text-[#F5EFE0]/70 max-w-2xl mx-auto mt-4">
            Velours vert émeraude, or impérial et rubans bordeaux — les écrins de gala
            ouvrent le cortège avant que les 13 maisons ne défilent le long du site.
          </p>
          <div className="gold-line w-72 mx-auto mt-6" />
        </div>

        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 items-stretch">
          <motion.figure
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="card-empire rounded-2xl overflow-hidden flex flex-col"
          >
            <img src={MAWLID.visuels[0].file} alt={MAWLID.visuels[0].nom} className="w-full flex-1 object-cover min-h-64" loading="lazy" />
            <figcaption className="p-5">
              <p className="font-display font-bold text-lg text-[#F5EFE0]">{MAWLID.visuels[0].nom}</p>
              <p className="text-sm text-[#F5EFE0]/60 mt-1">{MAWLID.visuels[0].desc}</p>
            </figcaption>
          </motion.figure>

          <div className="grid sm:grid-cols-2 gap-4">
            {coffrets.map((p) => (
              <div key={p.num} className="card-bordeaux rounded-2xl overflow-hidden flex flex-col">
                <img src={p.file} alt={p.name} className="w-full aspect-square object-cover" loading="lazy" />
                <div className="p-4 flex flex-col gap-2 flex-1">
                  <h3 className="font-display font-bold text-[#F5EFE0] leading-snug">{p.name}</h3>
                  <div className="mt-auto flex items-center justify-between gap-2">
                    <span className="font-display text-xl font-bold gold-text">{formatPrix(prixProduit(p.num))}</span>
                    <button
                      onClick={() => onAdd(bouye, p)}
                      className="gold-btn px-4 py-2 rounded-xl font-semibold text-xs inline-flex items-center gap-1.5"
                      aria-label={"Ajouter " + p.name + " au panier"}
                    >
                      <Gift className="h-3.5 w-3.5" /> Offrir
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────── Bloc — vitrine finale de l'Empire ─────────────── */
interface VitrineProps {
  onNavMarques: () => void;
}

export function BlockVitrine({ onNavMarques }: VitrineProps) {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24" aria-label="La vitrine finale de l'Empire">
      <div className="grid md:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <div className="card-empire rounded-2xl overflow-hidden">
            <img
              src="/produits/empire-vitrine-finale/101_vitrine-finale-empire-13-marques.png"
              alt="La vitrine finale réunissant les 13 marques en fer à cheval"
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="space-y-5">
          <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> La salle du trône
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[#F5EFE0] leading-tight">
            La Vitrine Finale <span className="gold-text">de l&rsquo;Empire</span>
          </h2>
          <p className="text-[#F5EFE0]/70 leading-relaxed">
            Treize échoppes de velours illuminées en fer à cheval, l&rsquo;amphore impériale
            Ndiamsyl Mody trônant sous le dais couronné — la réunion solennelle de tout
            un empire, peinte avant de figurer sur les étagères de la boutique.
          </p>
          <ul className="space-y-2 text-sm text-[#F5EFE0]/65">
            <li>· Chaque niche bruisse de la couleur d&rsquo;une marque</li>
            <li>· Couronne royale et ruban ivoire sur chaque étiquette</li>
            <li>· Signature complète apposée sur l&rsquo;ensemble des créations</li>
          </ul>
          <button onClick={onNavMarques} className="gold-btn px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2">
            Visiter les échoppes <Sparkles className="h-4 w-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────── Bloc — vidéo impériale ─────────────── */
export function BlockVideo() {
  return (
    <section className="border-t border-[#D4AF37]/15" aria-label="La vidéo impériale">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="text-center mb-10">
          <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3 flex items-center justify-center gap-2">
            <Play className="h-4 w-4" /> Le film du site
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#F5EFE0]">
            Une <span className="gold-text">vidéo impériale</span> — Mawlid Naby & Gamou
          </h2>
          <p className="text-[#F5EFE0]/60 max-w-xl mx-auto mt-3">
            Des lanternes dorées aux coffrets de gala : {VIDEO.duree} de cinématique
            à travers les couleurs de l&rsquo;Empire.
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="card-empire rounded-3xl overflow-hidden shadow-[0_30px_90px_rgba(0,0,0,0.55)]"
        >
          <video controls preload="metadata" poster={VIDEO.poster} className="w-full aspect-video bg-black" aria-label={VIDEO.titre}>
            <source src={VIDEO.file} type="video/mp4" />
            Votre navigateur ne peut pas lire la vidéo impériale.
          </video>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────── Bloc — portails morphing ─────────────── */
export function BlockPortails() {
  return (
    <section className="border-t border-[#D4AF37]/15" aria-label="Les 8 portails partenaires">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
        <div className="text-center mb-10">
          <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3">Navigation morphing</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#F5EFE0]">
            Les <span className="gold-text">8 Portails</span> du Réseau
          </h2>
          <p className="text-[#F5EFE0]/60 max-w-2xl mx-auto mt-3">
            Passez la main sur un bouton : du rectangle d&rsquo;émeraude au sceau bordeaux,
            sa forme se métamorphose puis vous emporte vers l&rsquo;autre rive du royaume.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PORTAILS.map((p) => (
            <a
              key={p.url}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="morph-btn rounded-2xl p-5 flex flex-col items-center text-center gap-2.5 focus:outline-none focus:ring-2 focus:ring-[#F6E27A]/70"
              aria-label={"Visiter le portail " + p.nom + " (nouvelle fenêtre)"}
            >
              <span className="font-display font-bold text-sm text-[#F5EFE0]">{p.nom}</span>
              <span className="text-xs text-[#F5EFE0]/60 leading-relaxed">{p.desc}</span>
              <span className="mt-auto pt-1.5 text-[11px] tracking-widest uppercase text-[#F6E27A]">{p.url.replace(/^https?:\/\/(www\.)?/i, "")}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────── Bloc — packs e-commerce ─────────────── */
interface PacksProps {
  onAdd: (idx: number) => void;
}

const PRIX_PACKS = [79.9, 129.9, 189.9, 34.9];

export function BlockPacks({ onAdd }: PacksProps) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24" aria-label="Packs e-commerce">
      <div className="text-center mb-10">
        <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3">Prêts pour la livraison impériale</p>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-[#F5EFE0]">
          Les <span className="gold-text">Packs E-commerce</span>
        </h2>
        <p className="text-[#F5EFE0]/60 max-w-2xl mx-auto mt-3">
          Colis scellés à la cire d&rsquo;or, sacs de velours brodés — l&rsquo;expérience
          d&rsquo;ouverture digne d&rsquo;un palais, livrée à votre porte.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {PACKS.slice(0, 4).map((p, i) => (
          <figure key={p.file} className="card-empire rounded-2xl overflow-hidden flex flex-col">
            <img src={p.file} alt={p.nom} className="w-full aspect-square object-cover" loading="lazy" />
            <figcaption className="p-4 flex flex-col gap-2 flex-1">
              <p className="font-display font-semibold text-[#F5EFE0]">{p.nom}</p>
              <p className="text-xs text-[#F5EFE0]/55 flex-1 line-clamp-3">{p.desc}</p>
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="font-display text-lg font-bold gold-text">{formatPrix(PRIX_PACKS[i])}</span>
                <button
                  onClick={() => onAdd(i)}
                  className="gold-btn px-3.5 py-2 rounded-xl font-semibold text-xs inline-flex items-center gap-1.5"
                  aria-label={"Ajouter " + p.nom + " au panier"}
                >
                  <ShoppingBag className="h-3.5 w-3.5" /> Panier
                </button>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

/* ─────────────── Bande de signature bordeaux (clôture des sites) ─────────────── */
export function BandeSignature() {
  return (
    <section className="bordeaux-band" aria-label="La signature de l'Empire">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 text-center">
        <Crown className="h-10 w-16 mx-auto mb-5" />
        <p className="font-display italic text-2xl md:text-4xl text-[#F5EFE0] leading-relaxed">
          « {SIGNATURE} »
        </p>
        <p className="text-sm text-[#F6E27A]/80 mt-5 max-w-xl mx-auto">
          Couronne royale, ruban de soie ivoire et signature complète — apposés sur
          chacune des {TOTAL_VISUELS} créations de l&rsquo;Empire, sur ce site comme sur les quatre autres.
        </p>
      </div>
    </section>
  );
}

